import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { AiTextarea } from '@/components/ui/ai-textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useTestimonialDialog } from '@/contexts/TestimonialDialogContext';
import { useTestimonials } from '@/hooks/useTestimonials';
import { useBrandManager } from '@/contexts/BrandProvider';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Star, Upload, X } from 'lucide-react';

export function GlobalTestimonialDialog() {
  const { isOpen, editingTestimonial, closeDialog } = useTestimonialDialog();
  const { currentBrandId } = useBrandManager();
  const { createTestimonial, updateTestimonial } = useTestimonials(currentBrandId || undefined);

  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [company, setCompany] = useState('');
  const [testimonial, setTestimonial] = useState('');
  const [rating, setRating] = useState(5);
  const [website, setWebsite] = useState('');
  const [isPublished, setIsPublished] = useState(true);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (editingTestimonial) {
      setName(editingTestimonial.name);
      setTitle(editingTestimonial.title || '');
      setCompany(editingTestimonial.company || '');
      setTestimonial(editingTestimonial.testimonial);
      setRating(editingTestimonial.rating || 5);
      setWebsite(editingTestimonial.website || '');
      setIsPublished(editingTestimonial.is_published);
      setCurrentImageUrl(editingTestimonial.image_url || null);
    } else {
      setName('');
      setTitle('');
      setCompany('');
      setTestimonial('');
      setRating(5);
      setWebsite('');
      setIsPublished(true);
      setCurrentImageUrl(null);
    }
    setImageFile(null);
  }, [editingTestimonial, isOpen]);

  const handleImageUpload = async () => {
    if (!imageFile || !currentBrandId) return null;

    setIsUploading(true);
    try {
      const fileExt = imageFile.name.split('.').pop();
      const fileName = `${currentBrandId}/${Date.now()}_${Math.random()}.${fileExt}`;
      
      const { error: uploadError, data } = await supabase.storage
        .from('testimonial-images')
        .upload(fileName, imageFile);

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('testimonial-images')
        .getPublicUrl(fileName);

      return publicUrl;
    } catch (error: any) {
      toast.error(`Failed to upload image: ${error.message}`);
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setCurrentImageUrl(null);
  };

  const handleSubmit = async () => {
    if (!currentBrandId || !name || !testimonial) {
      toast.error('Please fill in all required fields');
      return;
    }

    setIsLoading(true);
    try {
      let imageUrl = currentImageUrl;
      
      if (imageFile) {
        imageUrl = await handleImageUpload();
      }

      const testimonialData: any = {
        brand_id: currentBrandId,
        name,
        title: title || null,
        company: company || null,
        testimonial,
        rating,
        website: website || null,
        is_published: isPublished,
        image_url: imageUrl,
      };

      if (editingTestimonial) {
        await updateTestimonial({ id: editingTestimonial.id, ...testimonialData });
      } else {
        await createTestimonial(testimonialData);
      }

      closeDialog();
    } catch (error) {
      console.error('Error saving testimonial:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={closeDialog} modal={true}>
      <DialogContent className="max-h-[90vh] overflow-y-auto max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {editingTestimonial ? 'Edit Testimonial' : 'Add Testimonial'}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="title">Title / Position</Label>
              <Input
                id="title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="CEO"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="company">Company</Label>
              <Input
                id="company"
                value={company}
                onChange={(e) => setCompany(e.target.value)}
                placeholder="Acme Inc"
              />
            </div>
            <div>
              <Label htmlFor="website">Website</Label>
              <Input
                id="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                placeholder="https://example.com"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="testimonial">Testimonial *</Label>
            <AiTextarea
              id="testimonial"
              value={testimonial}
              onChange={(e) => setTestimonial(e.target.value)}
              placeholder="Share your experience..."
              rows={4}
              fieldContext={{
                fieldName: 'testimonial',
                pageContext: 'Customer Testimonial',
                brandId: currentBrandId,
                additionalContext: `Customer: ${name}${company ? `, Company: ${company}` : ''}${title ? `, Title: ${title}` : ''}`,
                currentValue: testimonial,
              }}
              onSuggestionAccepted={(suggestion) => {
                setTestimonial(suggestion);
              }}
            />
          </div>

          <div>
            <Label>Rating</Label>
            <div className="flex items-center gap-2 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`h-6 w-6 cursor-pointer transition-colors ${
                    star <= rating
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300 hover:text-yellow-200'
                  }`}
                  onClick={() => setRating(star)}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>Photo</Label>
            <div className="mt-2">
              {currentImageUrl && !imageFile && (
                <div className="relative inline-block mb-2">
                  <img
                    src={currentImageUrl}
                    alt="Current testimonial"
                    className="w-24 h-24 rounded-full object-cover border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                    onClick={handleRemoveImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {imageFile && (
                <div className="relative inline-block mb-2">
                  <img
                    src={URL.createObjectURL(imageFile)}
                    alt="Preview"
                    className="w-24 h-24 rounded-full object-cover border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0"
                    onClick={() => setImageFile(null)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
              {!currentImageUrl && !imageFile && (
                <div className="flex items-center gap-2">
                  <label htmlFor="image-upload">
                    <Button type="button" variant="outline" size="sm" asChild>
                      <span>
                        <Upload className="h-4 w-4 mr-2" />
                        Upload Photo
                      </span>
                    </Button>
                  </label>
                  <input
                    id="image-upload"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) setImageFile(file);
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={isPublished}
              onCheckedChange={setIsPublished}
              id="published"
            />
            <Label htmlFor="published" className="cursor-pointer">
              Publish immediately
            </Label>
          </div>

          <Button
            onClick={handleSubmit}
            disabled={isLoading || isUploading || !name || !testimonial}
            className="w-full"
          >
            {isLoading || isUploading
              ? 'Saving...'
              : editingTestimonial
              ? 'Update Testimonial'
              : 'Create Testimonial'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
